import * as THREE from 'three';
import type { SceneAssets } from './assets';

// A numbered cube. The number is baked onto a transparent canvas texture and laid flat on the
// cube's top face. The body mirrors the Unity placed cube (BlockMat): NO albedo texture — a smooth,
// light off-white solid with dark numerals.

const numberCache = new Map<number, THREE.CanvasTexture>();

export function numberTexture(value: number): THREE.CanvasTexture {
  const cached = numberCache.get(value);
  if (cached) return cached;
  const size = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = 'rgba(38, 24, 12, 0.92)';
  const fontSize = value >= 100 ? 52 : value >= 10 ? 66 : 80;
  ctx.font = `700 ${fontSize}px Cinzel, Georgia, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(value), size / 2, size / 2 + 4);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  numberCache.set(value, tex);
  return tex;
}

const BODY = 0.92; // cube body size (slight gap between cells)
const bodyGeo = new THREE.BoxGeometry(BODY, BODY, BODY);
const labelGeo = new THREE.PlaneGeometry(0.66, 0.66);

export class CubeMesh {
  group = new THREE.Group();
  value: number;
  private body: THREE.Mesh;
  private label: THREE.Mesh;

  constructor(value: number, _assets?: SceneAssets) {
    this.value = value;
    const mat = new THREE.MeshStandardMaterial({
      color: 0xe9e1d2, // smooth light off-white (Unity BlockMat has no texture, smoothness ~0.47)
      roughness: 0.45,
      metalness: 0.0,
    });
    this.body = new THREE.Mesh(bodyGeo, mat);
    this.body.castShadow = true;
    this.body.receiveShadow = true;
    this.group.add(this.body);

    const labelMat = new THREE.MeshBasicMaterial({
      map: numberTexture(value),
      transparent: true,
      depthWrite: false,
    });
    this.label = new THREE.Mesh(labelGeo, labelMat);
    this.label.rotation.x = -Math.PI / 2;
    this.label.position.y = BODY / 2 + 0.012;
    this.group.add(this.label);
  }

  setValue(value: number): void {
    this.value = value;
    (this.label.material as THREE.MeshBasicMaterial).map = numberTexture(value);
    (this.label.material as THREE.MeshBasicMaterial).needsUpdate = true;
  }

  dispose(): void {
    this.group.removeFromParent();
    (this.body.material as THREE.Material).dispose();
    (this.label.material as THREE.Material).dispose();
  }
}
